import { Plus, Trash2 } from 'lucide-react';
import Field from '@/components/common/Field.jsx';
import { purchaseFormActions, usePurchaseForm } from '@/state/forms/purchaseFormSlice.js';

const actions = purchaseFormActions();

const ivaOptions = [0, 10.5, 21, 27];

function toNumber(value) {
  if (typeof value === 'number') return value;
  if (!value) return 0;
  const normalized = value.toString().replace(/[^0-9,.-]/g, '').replace(/\./g, '').replace(',', '.');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

export default function ItemsTable() {
  const items = usePurchaseForm(state => state.items);

  const handleChange = (item, field, value) => {
    const updated = { ...item, [field]: field === 'alicuotaIva' ? Number(value) : value };
    if (field === 'cantidad' || field === 'precio') {
      const cantidad = toNumber(field === 'cantidad' ? value : item.cantidad);
      const precio = toNumber(field === 'precio' ? value : item.precio);
      updated.subtotal = Number((cantidad * precio).toFixed(2));
    }
    actions.updateItem(item.id, updated);
  };

  return (
    <section className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.75)] p-6 shadow-[0_20px_40px_rgba(var(--shadow),0.14)]">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Items</h2>
          <p className="text-sm text-[rgba(var(--muted),0.85)]">Agregá el detalle opcional de conceptos para trazabilidad interna.</p>
        </div>
        <button
          type="button"
          onClick={() => actions.addItem()}
          className="inline-flex items-center gap-2 rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow-[0_12px_26px_rgba(var(--shadow),0.16)] transition hover:bg-[rgb(var(--brand-700))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          Añadir ítem
        </button>
      </div>

      <div className="mt-6 overflow-x-auto">
        <table className="min-w-full divide-y divide-[rgba(var(--border),0.45)] text-sm text-[rgb(var(--fg))]">
          <thead className="bg-[rgba(var(--panel),0.9)] text-xs font-semibold uppercase text-[rgba(var(--muted),0.75)]">
            <tr>
              <th className="px-4 py-3 text-left">Código</th>
              <th className="px-4 py-3 text-left">Descripción</th>
              <th className="px-4 py-3 text-left">Cantidad</th>
              <th className="px-4 py-3 text-left">Precio</th>
              <th className="px-4 py-3 text-left">IVA</th>
              <th className="px-4 py-3 text-left">Subtotal</th>
              <th className="px-4 py-3" aria-label="Acciones" />
            </tr>
          </thead>
          <tbody className="divide-y divide-[rgba(var(--border),0.35)]">
            {items.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-sm text-[rgba(var(--muted),0.85)]">
                  Sin items cargados. Podés dejarlos vacíos o sumar tus conceptos.
                </td>
              </tr>
            ) : (
              items.map(item => (
                <tr key={item.id} className="transition hover:bg-[rgba(var(--panel),0.75)]">
                  <td className="px-4 py-3">
                    <Field>
                      <input
                        type="text"
                        value={item.codigo || ''}
                        onChange={event => handleChange(item, 'codigo', event.target.value)}
                        className="w-full rounded-xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.55)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                      />
                    </Field>
                  </td>
                  <td className="px-4 py-3">
                    <Field>
                      <input
                        type="text"
                        value={item.descripcion || ''}
                        onChange={event => handleChange(item, 'descripcion', event.target.value)}
                        className="w-full rounded-xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.55)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                      />
                    </Field>
                  </td>
                  <td className="px-4 py-3">
                    <Field>
                      <input
                        type="number"
                        value={item.cantidad}
                        min={0}
                        onChange={event => handleChange(item, 'cantidad', event.target.value)}
                        className="w-full rounded-xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.55)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                      />
                    </Field>
                  </td>
                  <td className="px-4 py-3">
                    <Field>
                      <input
                        type="number"
                        value={item.precio}
                        min={0}
                        onChange={event => handleChange(item, 'precio', event.target.value)}
                        className="w-full rounded-xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.55)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                      />
                    </Field>
                  </td>
                  <td className="px-4 py-3">
                    <Field>
                      <select
                        value={item.alicuotaIva}
                        onChange={event => handleChange(item, 'alicuotaIva', event.target.value)}
                        className="w-full rounded-xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.55)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                      >
                        {ivaOptions.map(option => (
                          <option key={option} value={option} className="bg-[rgb(var(--bg))] text-[rgb(var(--fg))]">
                            {option}%
                          </option>
                        ))}
                      </select>
                    </Field>
                  </td>
                  <td className="px-4 py-3">
                    <Field>
                      <input
                        type="number"
                        value={item.subtotal || 0}
                        readOnly
                        className="w-full rounded-xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.45)] px-3 py-2 text-sm font-semibold text-[rgb(var(--fg))] focus:outline-none"
                      />
                    </Field>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => actions.removeItem(item.id)}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.6)] text-[rgba(var(--muted),0.9)] transition hover:text-[rgb(var(--brand))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                      <span className="sr-only">Eliminar item</span>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
